import {
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GpSurfacesMapContentMode,
  GpSurfacesMapProjection,
  GpSurfacesMapSamplerCoverage,
  GpSurfacesMapSummary,
  GpSurfacesMapViewMode,
} from '../../../lib/types/gp-surfaces-map';
import { formatHaFr } from '../../../lib/utils/gp-surfaces-format';

import {
  GpSurfacesContextTable,
  GpSurfacesContextTableRow,
} from './gp-surfaces-context-table';
import { GpSurfacesSamplingMetricsTables } from './gp-surfaces-sampling-metrics-tables';

import { DepartmentFeatureProperties } from '@map';

type GpSurfacesMapSidebarProps = {
  contentMode: GpSurfacesMapContentMode;
  summary?: GpSurfacesMapSummary;
  projection?: GpSurfacesMapProjection | null;
  samplerCoverage?: GpSurfacesMapSamplerCoverage | null;
  viewMode: GpSurfacesMapViewMode;
  activeDepartment: DepartmentFeatureProperties | null;
  samplerCoverageOrgTable?: ReactNode;
  samplerCoverageDeptTable?: ReactNode;
};

export const GpSurfacesMapSidebar: FC<GpSurfacesMapSidebarProps> = ({
  contentMode,
  summary,
  projection,
  samplerCoverage,
  viewMode,
  activeDepartment,
  samplerCoverageOrgTable,
  samplerCoverageDeptTable,
}) => {
  const { t } = useTranslation();
  const showProjection = viewMode === 'with_projection' && projection;
  const isSamplerMode = contentMode === 'sampler_coverage';

  const activeSamplerDepartment = activeDepartment
    ? samplerCoverage?.departments.find(
        (department) => department.code === activeDepartment.code,
      )
    : undefined;

  const headlineMetrics = showProjection ? projection.summary : summary;

  const departmentMetrics = activeDepartment
    ? {
        carbon_points: activeDepartment.carbon_points,
        granulo_points: activeDepartment.granulo_points ?? 0,
        total_sampling_points: activeDepartment.total_sampling_points ?? 0,
        analysis_count: activeDepartment.analysis_count,
        granulo_analysis_count: activeDepartment.granulo_analysis_count ?? 0,
        total_analysis_count: activeDepartment.total_analysis_count ?? 0,
      }
    : null;

  const departmentContextRows = useMemo(() => {
    if (!activeDepartment) {
      return [];
    }

    const rows: GpSurfacesContextTableRow[] = [
      {
        key: 'dept-area',
        label: showProjection
          ? t('surfacesMap.tables.combinedArea')
          : t('surfacesMap.summary.totalArea'),
        value: showProjection
          ? t('surfacesMap.summary.areaValue', {
              value: formatHaFr(
                activeDepartment.combined_area ??
                  activeDepartment.actual_area ??
                  activeDepartment.total_area,
              ),
            })
          : t('surfacesMap.summary.areaValue', {
              value: formatHaFr(activeDepartment.total_area),
            }),
        emphasize: true,
      },
    ];

    if (showProjection) {
      rows.push({
        key: 'dept-actual-area',
        label: t('surfacesMap.tables.actualArea'),
        value: t('surfacesMap.summary.areaValue', {
          value: formatHaFr(
            activeDepartment.actual_area ?? activeDepartment.total_area,
          ),
        }),
      });
    }

    if ((activeDepartment.projected_volume_t ?? 0) > 0) {
      rows.push({
        key: 'dept-volume',
        label: t('surfacesMap.tables.projectedVolume'),
        value: t('surfacesMap.projection.volumeValue', {
          value: activeDepartment.projected_volume_t?.toFixed(0) ?? '0',
        }),
      });
    }

    rows.push({
      key: 'dept-parcelles',
      label: t('surfacesMap.tables.parcelles'),
      value: String(activeDepartment.parcelle_count),
    });

    return rows;
  }, [activeDepartment, showProjection, t]);

  return (
    <Paper
      h="100%"
      mih={0}
      p={0}
      radius="sm"
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      withBorder
    >
      <ScrollArea flex={1} mih={0} p="md" type="auto">
        <Stack gap="lg" mih="100%">
          <Title order={4}>
            {isSamplerMode
              ? t('surfacesMap.samplerCoverage.sidebarTitle')
              : t('surfacesMap.sidebar.title')}
          </Title>

          {isSamplerMode ? (
            <Stack gap="lg">
              <Stack gap="xs">
                <Group gap={6} wrap="nowrap">
                  <Text fw={700} size="sm">
                    {t('surfacesMap.samplerCoverage.orgRollupTitle')}
                  </Text>
                  <Tooltip
                    label={t('surfacesMap.samplerCoverage.orgRollupHint')}
                    multiline
                    w={280}
                  >
                    <IconInfoCircle
                      aria-hidden
                      color="var(--mantine-color-dimmed)"
                      size={16}
                      style={{ flexShrink: 0 }}
                    />
                  </Tooltip>
                </Group>
                {samplerCoverageOrgTable}
              </Stack>
              {samplerCoverageDeptTable ? (
                <>
                  <Divider />
                  <Stack gap="xs">
                    <Text c="dimmed" fw={600} size="xs" tt="uppercase">
                      {t('surfacesMap.samplerCoverage.deptDetailTitle')}
                    </Text>
                    {activeDepartment ? (
                      <Text fw={600} size="sm">
                        {activeDepartment.code} — {activeDepartment.name}
                      </Text>
                    ) : null}
                    {samplerCoverageDeptTable}
                    {activeSamplerDepartment &&
                    !activeSamplerDepartment.is_complete ? (
                      <Text c="orange" size="xs">
                        {t('surfacesMap.samplerCoverage.incompleteWarning', {
                          percent: activeSamplerDepartment.uncovered_share_percent,
                        })}
                      </Text>
                    ) : null}
                  </Stack>
                </>
              ) : (
                <Text c="dimmed" size="xs">
                  {t('surfacesMap.samplerCoverage.selectDeptHint')}
                </Text>
              )}
            </Stack>
          ) : null}

          {!isSamplerMode && headlineMetrics ? (
            <Stack gap="md">
              {showProjection ? (
                <GpSurfacesContextTable
                  rows={[
                    {
                      key: 'combined-area',
                      label: t('surfacesMap.tables.combinedArea'),
                      value: t('surfacesMap.summary.areaValue', {
                        value: formatHaFr(projection.summary.combined_area),
                      }),
                      emphasize: true,
                    },
                    {
                      key: 'projected-area',
                      label: t('surfacesMap.projection.area'),
                      value: t('surfacesMap.summary.areaValue', {
                        value: formatHaFr(projection.summary.projected_area),
                      }),
                    },
                    {
                      key: 'projected-volume',
                      label: t('surfacesMap.tables.projectedVolume'),
                      value: t('surfacesMap.projection.volumeValue', {
                        value: projection.summary.projected_volume_t.toFixed(0),
                      }),
                    },
                  ]}
                  title={t('surfacesMap.projection.title')}
                />
              ) : null}
              <GpSurfacesSamplingMetricsTables metrics={headlineMetrics} />
            </Stack>
          ) : null}

          {!isSamplerMode && summary ? (
            <GpSurfacesContextTable
              muted={Boolean(showProjection)}
              rows={[
                {
                  key: 'total-area',
                  label: t('surfacesMap.summary.totalArea'),
                  value: t('surfacesMap.summary.areaValue', {
                    value: formatHaFr(summary.total_area),
                  }),
                  emphasize: !showProjection,
                },
                {
                  key: 'ilot-count',
                  label: t('surfacesMap.summary.ilotCount'),
                  value: String(summary.ilot_count),
                },
                {
                  key: 'parcelle-count',
                  label: t('surfacesMap.summary.parcelleCount'),
                  value: String(summary.parcelle_count),
                },
                {
                  key: 'department-count',
                  label: t('surfacesMap.summary.departmentCount'),
                  value: String(summary.department_count),
                },
              ]}
              title={
                showProjection
                  ? t('surfacesMap.summary.actualTitle')
                  : t('surfacesMap.tables.contextSection')
              }
            />
          ) : null}

          {activeDepartment && !isSamplerMode && departmentMetrics ? (
            <>
              <Divider />
              <Stack gap="md">
                <Text fw={600} size="sm">
                  {t('surfacesMap.sidebar.selectedDepartment')}
                </Text>
                <Text fw={600}>
                  {activeDepartment.code} — {activeDepartment.name}
                </Text>
                <GpSurfacesContextTable rows={departmentContextRows} />
                <GpSurfacesSamplingMetricsTables metrics={departmentMetrics} />
              </Stack>
            </>
          ) : null}
        </Stack>
      </ScrollArea>
    </Paper>
  );
};
